'use client';

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  cancelWithdraw,
  createDepositAddress,
  fetchBlockchains,
  fetchBonusBurnPreview,
  fetchCoins,
  fetchDepositAddress,
  fetchTopHolders,
  fetchTransactions,
  fetchWithdrawSettings,
  initiateWithdraw,
  resendWithdrawCode,
  submitWithdraw,
  type TransactionsResult,
} from '@/lib/api/transaction/client';
import type {
  Blockchain,
  BonusBurnPreview,
  Coin,
  DepositAddress,
  InitiateWithdrawResult,
  TopHoldersResponse,
  WithdrawSettings,
} from '@/lib/api/transaction/models';
import type {
  DepositAddressRequestDto,
  InitiateWithdrawRequestDto,
  SubmitWithdrawRequestDto,
  TopHoldersQueryParams,
  TransactionsQueryParams,
} from '@/lib/api/transaction/types';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { FEATURE_FLAGS } from '@/lib/config/features';

export const TRANSACTIONS_QK = 'transactions';
export const COINS_QK = 'coins';
export const TOP_HOLDERS_QK = 'top-holders';
export const BLOCKCHAINS_QK = 'blockchains';
export const WITHDRAW_SETTINGS_QK = 'withdraw-settings';
export const BONUS_BURN_PREVIEW_QK = 'bonus-burn-preview';
export const DEPOSIT_ADDRESS_QK = 'deposit-address';

export function useTransactions(params?: TransactionsQueryParams) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<TransactionsResult, Error>({
    queryKey: [TRANSACTIONS_QK, params],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchTransactions(params);
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useInfiniteTransactions(params?: TransactionsQueryParams) {
  const { isAuthenticated } = useAuthContext();
  const pageSize = params?.size ?? 20;

  return useInfiniteQuery({
    queryKey: [TRANSACTIONS_QK, 'infinite', params],
    queryFn: async ({ pageParam = 0 }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchTransactions({ ...params, page: pageParam, size: pageSize });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useCoins() {
  const { isAuthenticated } = useAuthContext();

  return useQuery<Coin[], Error>({
    queryKey: [COINS_QK],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchCoins();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useTopHolders(params: TopHoldersQueryParams) {
  return useQuery<TopHoldersResponse | null, Error>({
    queryKey: [TOP_HOLDERS_QK, params],
    queryFn: () => fetchTopHolders(params),
    enabled: Boolean(params.eventTokenId),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

export function useBlockchains(coinId?: string) {
  return useQuery<Blockchain[], Error>({
    queryKey: [BLOCKCHAINS_QK, coinId],
    queryFn: () => fetchBlockchains(coinId!),
    enabled: Boolean(coinId),
    staleTime: 5 * 60 * 1000, // 5 minutes — blockchain list rarely changes
    refetchOnWindowFocus: false,
  });
}

/*
 * Withdraw hooks
 */

export function useWithdrawSettings(coinId?: string, blockchainId?: string) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<WithdrawSettings, Error>({
    queryKey: [WITHDRAW_SETTINGS_QK, coinId, blockchainId],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchWithdrawSettings(coinId!, blockchainId!);
    },
    enabled: isAuthenticated && Boolean(coinId) && Boolean(blockchainId),
    staleTime: 30_000, // 30s — settings rarely change mid-session
  });
}

export function useBonusBurnPreview(amountUsd: number) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<BonusBurnPreview, Error>({
    queryKey: [BONUS_BURN_PREVIEW_QK, amountUsd],
    queryFn: () => fetchBonusBurnPreview(amountUsd),
    // Bonus is a promo payout — with promocodes off there is nothing to burn.
    enabled: FEATURE_FLAGS.ENABLE_PROMOCODES && isAuthenticated && amountUsd > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false, // best-effort UI — on error the warning is simply hidden
  });
}

export function useInitiateWithdraw() {
  const { isAuthenticated } = useAuthContext();

  return useMutation<InitiateWithdrawResult, Error, InitiateWithdrawRequestDto>({
    mutationFn: async (payload) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return initiateWithdraw(payload);
    },
  });
}

export function useSubmitWithdraw() {
  const { isAuthenticated } = useAuthContext();

  return useMutation<void, Error, { transactionId: string } & SubmitWithdrawRequestDto>({
    mutationFn: async ({ transactionId, ...payload }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return submitWithdraw(transactionId, payload);
    },
    // No invalidation — WalletSocketProvider handles TRANSACTION_UPDATE via WebSocket
  });
}

export function useCancelWithdraw() {
  const { isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (transactionId) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return cancelWithdraw(transactionId);
    },
    onSuccess: (_data, transactionId) => {
      const queries = queryClient.getQueryCache().findAll({ queryKey: [TRANSACTIONS_QK] });
      queries.forEach((query) => {
        const data = query.state.data;
        if (!data) return;

        if ('pages' in (data as Record<string, unknown>)) {
          queryClient.setQueryData<InfiniteData<TransactionsResult>>(query.queryKey, (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                transactions: page.transactions.filter((t) => t.id !== transactionId),
              })),
            };
          });
        } else if ('transactions' in (data as Record<string, unknown>)) {
          queryClient.setQueryData<TransactionsResult>(query.queryKey, (old) => {
            if (!old) return old;
            return {
              ...old,
              transactions: old.transactions.filter((t) => t.id !== transactionId),
            };
          });
        }
      });
    },
  });
}

export function useResendWithdrawCode() {
  const { isAuthenticated } = useAuthContext();

  return useMutation<void, Error, string>({
    mutationFn: async (transactionId) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return resendWithdrawCode(transactionId);
    },
  });
}

/*
 * Deposit hooks
 */

export function useCreateDepositAddress() {
  const { isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation<DepositAddress, Error, DepositAddressRequestDto>({
    mutationFn: async (payload) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return createDepositAddress(payload);
    },
    onSuccess: (_data, variables) => {
      // Cache by coin+blockchain so repeated selections don't re-fetch
      queryClient.setQueryData([DEPOSIT_ADDRESS_QK, variables.c, variables.b], _data);
    },
  });
}

export function useDepositAddress(coinId?: string, blockchainId?: string) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<DepositAddress, Error>({
    queryKey: [DEPOSIT_ADDRESS_QK, coinId, blockchainId],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchDepositAddress(coinId!, blockchainId!);
    },
    enabled: isAuthenticated && Boolean(coinId) && Boolean(blockchainId),
    staleTime: Infinity, // Address doesn't change once created
  });
}
